import { isLeft, isRight } from 'fp-ts/Either';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { useAsyncData } from '../hooks/useAsyncData';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useTRPC } from '../hooks/useTRPC';
import type { RightResult, RouterOutputs } from '../types/TRPC';
import { captureAndReportError } from '../utils/errorReporting';

type Overview = RightResult<RouterOutputs['databaseApi']['getOverview']>;
type Field = Overview['fields'][number];
type Key = Overview['keys'][number];
type LogsPage = RightResult<RouterOutputs['databaseApi']['listQueryLogs']>;
type QueryLog = LogsPage['logs'][number];

const EMPTY_OVERVIEW: Overview = {
  endpoint: '/api/database/v1',
  fields: [],
  keys: [],
};

const DATASET_LABELS: Record<Field['dataset'], string> = {
  public_dapps: 'Published dApps',
  published_releases: 'Published releases',
  dapps: 'All dApps',
  releases: 'All releases',
  publication_tickets: 'Publication tickets',
  publishers: 'Publishers',
  kyb_submissions: 'KYB submissions',
  websdk_events: 'WebSDK events',
  automated_review_runs: 'Automated review runs',
  automated_review_check_results: 'Automated review checks',
  ticket_processing_events: 'Ticket-processing events',
  dapp_daily_metrics: 'Daily analytics',
};

const ALWAYS_HIDDEN = [
  'Passwords, verification codes, tokens, key hashes, and signatures',
  'Email addresses, phone numbers, identity documents, and verification free text',
  'IP addresses, wallets, device IDs, webhook bodies, and raw telemetry payloads',
  'Internal notes, review excerpts, message content, and raw Sumsub payloads',
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : 'Never';
}

function keyStatus(key: Key): 'Active' | 'Expired' | 'Revoked' {
  if (key.revokedAt) {
    return 'Revoked';
  }
  return new Date(key.expiresAt).getTime() <= Date.now() ? 'Expired' : 'Active';
}

function SecretBanner({
  secret,
  onDismiss,
}: {
  secret: string;
  onDismiss: () => void;
}) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      toast.success('API key copied');
    } catch {
      toast.error('Could not copy the API key');
    }
  };

  return (
    <section className="rounded-xl border border-amber-500/30 bg-amber-950/30 p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-medium text-amber-100">Save this key now</h2>
          <p className="mt-1 text-sm text-amber-200/70">
            It is stored as a hash and will not be shown again.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void copy()}>
            Copy key
          </Button>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </div>
      <code className="mt-4 block break-all rounded-lg bg-black/30 p-3 text-sm text-amber-50">
        {secret}
      </code>
    </section>
  );
}

function FieldGroup({
  dataset,
  fields,
  savingFieldId,
  onToggle,
}: {
  dataset: Field['dataset'];
  fields: Field[];
  savingFieldId: string | null;
  onToggle: (field: Field) => void;
}) {
  const exposed = fields.filter((field) => field.enabled).length;

  return (
    <section className="rounded-xl border border-neutral-800 bg-neutral-900/60">
      <div className="flex items-center justify-between border-b border-neutral-800 px-5 py-4">
        <div>
          <h3 className="font-medium text-white">{DATASET_LABELS[dataset]}</h3>
          <p className="mt-1 text-xs text-neutral-500">
            {dataset} · {exposed} exposed · {fields.length - exposed} hidden
          </p>
        </div>
      </div>
      <div className="divide-y divide-neutral-800">
        {fields.map((field) => (
          <label
            key={field.id}
            className="flex cursor-pointer items-start gap-4 px-5 py-4"
          >
            <input
              type="checkbox"
              aria-label={`${field.enabled ? 'Hide' : 'Expose'} ${field.label}`}
              checked={field.enabled}
              disabled={savingFieldId !== null}
              onChange={() => onToggle(field)}
              className="mt-1 size-4 accent-teal-500"
            />
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-neutral-100">
                  {field.label}
                </span>
                <code className="rounded bg-neutral-950 px-1.5 py-0.5 text-xs text-neutral-400">
                  {field.field}
                </code>
                <span className="text-xs text-neutral-600">
                  {field.dataType}
                </span>
              </span>
              <span className="mt-1 block text-sm text-neutral-500">
                {field.description}
              </span>
            </span>
            <span
              className={
                field.enabled
                  ? 'text-xs font-medium text-emerald-400'
                  : 'text-xs font-medium text-neutral-500'
              }
            >
              {savingFieldId === field.id
                ? 'Saving…'
                : field.enabled
                  ? 'Exposed'
                  : 'Hidden'}
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

function QueryLogRow({ log }: { log: QueryLog }) {
  const statusClass =
    log.status === 'success'
      ? 'text-emerald-400'
      : log.status === 'rejected'
        ? 'text-amber-400'
        : 'text-red-400';

  return (
    <tr className="border-t border-neutral-800 align-top">
      <td className="whitespace-nowrap px-4 py-3 text-xs text-neutral-400">
        {formatDate(log.created)}
        <code className="mt-1 block max-w-44 truncate text-neutral-600">
          {log.requestId}
        </code>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-neutral-200">
          {log.apiKey?.label ?? 'Deleted key'}
        </div>
        <code className="text-xs text-neutral-600">
          {log.apiKey?.keyPrefix ?? 'unknown'}
        </code>
      </td>
      <td className="px-4 py-3">
        <code className="text-xs text-neutral-300">{log.dataset}</code>
        <div className="mt-1 text-xs text-neutral-600">
          {log.fields.length > 0 ? log.fields.join(', ') : 'No selected fields'}
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-neutral-500">
        {log.filterNames.length > 0 ? log.filterNames.join(', ') : 'None'}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-xs">
        <span className={statusClass}>{log.status}</span>
        <div className="mt-1 text-neutral-600">
          {log.statusCode} · {log.rowCount ?? '–'} rows · {log.durationMs} ms
        </div>
        {log.errorCode && (
          <code className="mt-1 block text-red-400/70">{log.errorCode}</code>
        )}
      </td>
    </tr>
  );
}

export function DatabaseApiPage() {
  const { canRender } = useRequireAuth({ requireRole: 'superadmin' });
  const trpc = useTRPC();
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [savingFieldId, setSavingFieldId] = useState<string | null>(null);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);

  const {
    data: overview,
    setData: setOverview,
    isLoading: overviewLoading,
    error: overviewError,
    run: runOverview,
  } = useAsyncData<Overview>({
    initialData: EMPTY_OVERVIEW,
    initialLoading: true,
  });
  const {
    data: logsPage,
    isLoading: logsLoading,
    error: logsError,
    run: runLogs,
  } = useAsyncData<LogsPage>({
    initialData: { logs: [], nextCursor: null },
    initialLoading: true,
  });

  usePageTitle('Database API | Publishing Portal');

  const loadOverview = useCallback(async () => {
    await runOverview(() => trpc.databaseApi.getOverview.query({}), {
      onSuccess: (result, helpers) => {
        if (isRight(result)) {
          helpers.setData(result.right);
        } else {
          helpers.setError(result.left.message);
        }
      },
      onError: (error, helpers) => {
        captureAndReportError(error, {
          operation: 'superadmin.databaseApi.getOverview',
          component: 'DatabaseApiPage',
        });
        helpers.setError('Failed to load database API settings');
      },
    });
  }, [runOverview, trpc]);

  const loadLogs = useCallback(
    async (cursor?: string) => {
      await runLogs(
        () =>
          trpc.databaseApi.listQueryLogs.query({
            cursor,
            limit: 50,
          }),
        {
          showLoadingState: true,
          onSuccess: (result, helpers) => {
            if (isRight(result)) {
              helpers.setData((current) => ({
                logs: cursor
                  ? [...current.logs, ...result.right.logs]
                  : result.right.logs,
                nextCursor: result.right.nextCursor,
              }));
            } else {
              helpers.setError(result.left.message);
            }
          },
          onError: (error, helpers) => {
            captureAndReportError(error, {
              operation: 'superadmin.databaseApi.listQueryLogs',
              component: 'DatabaseApiPage',
            });
            helpers.setError('Failed to load database API query logs');
          },
        },
      );
    },
    [runLogs, trpc],
  );

  useEffect(() => {
    if (canRender) {
      void Promise.all([loadOverview(), loadLogs()]);
    }
  }, [canRender, loadLogs, loadOverview]);

  const fieldsByDataset = useMemo(
    () =>
      Object.entries(DATASET_LABELS).map(([dataset]) => ({
        dataset: dataset as Field['dataset'],
        fields: overview.fields.filter((field) => field.dataset === dataset),
      })),
    [overview.fields],
  );

  const exposedCount = overview.fields.filter((field) => field.enabled).length;
  const activeKeyCount = overview.keys.filter(
    (key) => keyStatus(key) === 'Active',
  ).length;

  const toggleField = useCallback(
    async (field: Field) => {
      setSavingFieldId(field.id);
      try {
        const result = await trpc.databaseApi.updateField.mutate({
          fieldId: field.id,
          enabled: !field.enabled,
        });
        if (isLeft(result)) {
          toast.error(result.left.message);
          return;
        }
        setOverview((current) => ({
          ...current,
          fields: current.fields.map((item) =>
            item.id === result.right.id ? result.right : item,
          ),
        }));
        toast.success(
          `${field.label} is now ${result.right.enabled ? 'exposed' : 'hidden'}`,
        );
      } catch (error) {
        captureAndReportError(error, {
          operation: 'superadmin.databaseApi.updateField',
          component: 'DatabaseApiPage',
          fieldId: field.id,
        });
        toast.error('Failed to update field exposure');
      } finally {
        setSavingFieldId(null);
      }
    },
    [setOverview, trpc],
  );

  const createKey = useCallback(async () => {
    if (!label.trim()) {
      toast.error('Enter a label for the API key');
      return;
    }

    setIsCreatingKey(true);
    try {
      const result = await trpc.databaseApi.createKey.mutate({
        label: label.trim(),
      });
      if (isLeft(result)) {
        toast.error(result.left.message);
        return;
      }
      setSecret(result.right.secret);
      setLabel('');
      setOverview((current) => ({
        ...current,
        keys: [result.right.key, ...current.keys],
      }));
      toast.success('Database API key created');
    } catch (error) {
      captureAndReportError(error, {
        operation: 'superadmin.databaseApi.createKey',
        component: 'DatabaseApiPage',
      });
      toast.error('Failed to create database API key');
    } finally {
      setIsCreatingKey(false);
    }
  }, [label, setOverview, trpc]);

  const revokeKey = useCallback(
    async (key: Key) => {
      if (!window.confirm(`Revoke "${key.label}"? This cannot be undone.`)) {
        return;
      }

      setRevokingKeyId(key.id);
      try {
        const result = await trpc.databaseApi.revokeKey.mutate({
          keyId: key.id,
        });
        if (isLeft(result)) {
          toast.error(result.left.message);
          return;
        }
        await loadOverview();
        toast.success(`Revoked "${key.label}"`);
      } catch (error) {
        captureAndReportError(error, {
          operation: 'superadmin.databaseApi.revokeKey',
          component: 'DatabaseApiPage',
          keyId: key.id,
        });
        toast.error('Failed to revoke database API key');
      } finally {
        setRevokingKeyId(null);
      }
    },
    [loadOverview, trpc],
  );

  if (!canRender) {
    return null;
  }

  return (
    <main className="min-h-screen bg-neutral-950 px-4 py-8 text-neutral-100 md:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="border-b border-neutral-800 pb-6">
          <div className="text-xs font-medium uppercase tracking-wider text-teal-400">
            Internal · Superadmin only · Read-only
          </div>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-white">
            Database API
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-neutral-400">
            Manage the reviewed fields available to analytical clients, issue
            expiring credentials, and inspect every authenticated API query.
            Callers cannot submit SQL or write application data.
          </p>
        </header>

        {overviewError && (
          <div className="rounded-lg border border-red-500/30 bg-red-950/30 p-4 text-sm text-red-200">
            {overviewError}
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-5">
            <div className="text-xs uppercase tracking-wide text-neutral-500">
              Exposed fields
            </div>
            <div className="mt-2 text-2xl font-semibold">{exposedCount}</div>
            <div className="mt-1 text-xs text-neutral-500">
              {overview.fields.length - exposedCount} reviewed fields hidden
            </div>
          </div>
          <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-5">
            <div className="text-xs uppercase tracking-wide text-neutral-500">
              Active keys
            </div>
            <div className="mt-2 text-2xl font-semibold">{activeKeyCount}</div>
            <div className="mt-1 text-xs text-neutral-500">
              Keys expire after 90 days
            </div>
          </div>
          <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-5">
            <div className="text-xs uppercase tracking-wide text-neutral-500">
              Endpoint
            </div>
            <code className="mt-2 block break-all text-sm text-teal-300">
              {overview.endpoint}
            </code>
            <div className="mt-1 text-xs text-neutral-500">
              Authenticate with x-api-key
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-5">
          <h2 className="font-medium text-white">Permanent safety boundary</h2>
          <p className="mt-2 text-sm text-neutral-400">
            The controls below can only toggle source-reviewed fields. All other
            database columns remain inaccessible to the API.
          </p>
          <ul className="mt-3 grid gap-2 text-sm text-neutral-500 md:grid-cols-2">
            {ALWAYS_HIDDEN.map((item) => (
              <li key={item} className="rounded-lg bg-neutral-950/60 px-3 py-2">
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-5">
          <h2 className="font-medium text-white">How to query</h2>
          <p className="mt-2 text-sm text-neutral-400">
            Send <code className="text-teal-300">x-api-key</code> with every
            request. Discover the current datasets and exposed fields before
            querying; hidden fields are rejected.
          </p>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-neutral-500">
                Discover
              </div>
              <code className="mt-2 block overflow-x-auto rounded-lg bg-neutral-950 p-3 text-xs text-neutral-300">
                GET {overview.endpoint}/datasets
              </code>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-neutral-500">
                Query
              </div>
              <code className="mt-2 block overflow-x-auto whitespace-pre rounded-lg bg-neutral-950 p-3 text-xs text-neutral-300">
                {`POST ${overview.endpoint}/query
{
  "dataset": "public_dapps",
  "fields": ["dappId", "name", "androidPackage"],
  "limit": 50
}`}
              </code>
            </div>
          </div>
        </section>

        <section>
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-white">Field exposure</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Changes apply immediately to the public field catalog and all
              subsequent queries.
            </p>
          </div>
          {overviewLoading ? (
            <div className="rounded-xl border border-neutral-800 p-12 text-center text-sm text-neutral-500">
              Loading field settings…
            </div>
          ) : (
            <div className="space-y-4">
              {fieldsByDataset.map(({ dataset, fields }) => (
                <FieldGroup
                  key={dataset}
                  dataset={dataset}
                  fields={fields}
                  savingFieldId={savingFieldId}
                  onToggle={(field) => void toggleField(field)}
                />
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-neutral-800 bg-neutral-900/60">
          <div className="border-b border-neutral-800 px-5 py-4">
            <h2 className="text-xl font-semibold text-white">API keys</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Use one key per client. Secrets are shown once, stored as hashes,
              and expire after 90 days.
            </p>
          </div>
          <div className="border-b border-neutral-800 p-5">
            <div className="flex flex-col gap-3 sm:flex-row">
              <label className="flex-1">
                <span className="sr-only">API key label</span>
                <input
                  value={label}
                  aria-label="API key label"
                  onChange={(event) => setLabel(event.target.value)}
                  maxLength={64}
                  placeholder="Client or integration name"
                  className="h-10 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 text-sm outline-none focus:border-teal-500"
                />
              </label>
              <Button
                onClick={() => void createKey()}
                disabled={isCreatingKey}
                className="bg-teal-600 text-white hover:bg-teal-500"
              >
                {isCreatingKey ? 'Creating…' : 'Create key'}
              </Button>
            </div>
          </div>
          {secret && (
            <div className="border-b border-neutral-800 p-5">
              <SecretBanner secret={secret} onDismiss={() => setSecret(null)} />
            </div>
          )}
          <div className="divide-y divide-neutral-800">
            {overview.keys.length === 0 ? (
              <div className="p-8 text-center text-sm text-neutral-500">
                No database API keys have been created.
              </div>
            ) : (
              overview.keys.map((key) => {
                const status = keyStatus(key);
                return (
                  <div
                    key={key.id}
                    className="grid gap-4 px-5 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{key.label}</span>
                        <span
                          className={
                            status === 'Active'
                              ? 'text-xs text-emerald-400'
                              : 'text-xs text-neutral-500'
                          }
                        >
                          {status}
                        </span>
                      </div>
                      <code className="mt-1 block text-xs text-neutral-600">
                        {key.keyPrefix}…
                      </code>
                    </div>
                    <div className="text-xs text-neutral-500">
                      <div>Created {formatDate(key.created)}</div>
                      <div>Expires {formatDate(key.expiresAt)}</div>
                      <div>Last used {formatDate(key.lastUsedAt)}</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={status !== 'Active' || revokingKeyId === key.id}
                      onClick={() => void revokeKey(key)}
                    >
                      {revokingKeyId === key.id ? 'Revoking…' : 'Revoke'}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="rounded-xl border border-neutral-800 bg-neutral-900/60">
          <div className="border-b border-neutral-800 px-5 py-4">
            <h2 className="text-xl font-semibold text-white">Query log</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Logs contain field and filter names, status, row count, and
              duration. Filter values, result data, SQL, and API secrets are
              never stored.
            </p>
          </div>
          {logsError && (
            <div className="border-b border-red-500/20 bg-red-950/30 px-5 py-3 text-sm text-red-200">
              {logsError}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead className="text-xs uppercase tracking-wide text-neutral-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Time</th>
                  <th className="px-4 py-3 font-medium">Key</th>
                  <th className="px-4 py-3 font-medium">Dataset and fields</th>
                  <th className="px-4 py-3 font-medium">Filters</th>
                  <th className="px-4 py-3 font-medium">Result</th>
                </tr>
              </thead>
              <tbody>
                {logsPage.logs.map((log) => (
                  <QueryLogRow key={log.id} log={log} />
                ))}
              </tbody>
            </table>
          </div>
          {logsPage.logs.length === 0 && !logsLoading && (
            <div className="p-8 text-center text-sm text-neutral-500">
              No authenticated database API queries have been recorded.
            </div>
          )}
          {(logsLoading || logsPage.nextCursor) && (
            <div className="border-t border-neutral-800 p-4 text-center">
              <Button
                variant="outline"
                disabled={logsLoading}
                onClick={() =>
                  logsPage.nextCursor && void loadLogs(logsPage.nextCursor)
                }
              >
                {logsLoading ? 'Loading…' : 'Load older queries'}
              </Button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
